import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, Paper, Typography, Box, Button, 
  Divider, Chip, Grid, Card, CardContent,
  List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import { 
  CheckCircle, LocalShipping, Payment, 
  Store, Print, ShoppingBag, ArrowBack,
  Discount, Savings
} from '@mui/icons-material';
import api from '../services/api';
import noPhoto from "../assets/no_photo.webp";

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await api.get(`/orders/${orderId}`);
        setOrder(response.data);
      } catch (err) {
        console.error('Error fetching order:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  // Calculate total savings from order items
  const totalSavings = order?.items?.reduce((total, item) => {
    return total + ((item.original_price || item.price) - item.price) * item.quantity;
  }, 0) || 0;

  // Calculate original total before discounts
  const originalTotal = order?.items?.reduce((total, item) => {
    return total + (item.original_price || item.price) * item.quantity;
  }, 0) || 0;

  if (loading) return (
    <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
      <Typography variant="h6">Loading order details...</Typography>
    </Container>
  );

    const statusTxt = {
  canceled: 'ملغي',
  pending: 'معلق',
  processing: 'قيد التحضير',
  shipped: 'قيد التوصيل',
  delivered: 'مكتمل'
};

  
  if (!order) return (
    <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
      <Typography variant="h6" color="error">Order not found</Typography>
      <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/')}>
        Continue Shopping
      </Button>
    </Container>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4, mb: 10 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
        <Typography variant="h3" fontWeight="bold" gutterBottom color="success.main">
          تم تأكيد الطلب !
        </Typography>
        <Typography variant="h6" color="text.secondary">
          شكرا لك على الشراء
        </Typography>
      </Box>

      <Grid container spacing={3} style={{marginRight:-16}}>
        {/* Order Summary Card */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            {/* Order Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  ملخص طلبك
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  طلب رقم #: {order.id}
                </Typography>
                <Chip 
                  label={statusTxt[order.status]} 
                  color={order.status === 'delivered' ? 'success' : order.status === 'canceled' ? 'error' : 'warning'}
                  sx={{ mt: 1 }}
                />
              </Box>
              <Typography variant="h6" color="primary">
                E.G.P {order.total_amount}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Savings Banner */}
            {totalSavings > 0 && (
              <Box sx={{ 
                backgroundColor: 'success.light', 
                p: 2, 
                borderRadius: 1, 
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Savings sx={{ fontSize: 40, color: 'success.dark' }} />
                <Box>
                  <Typography variant="h6" color="success.dark" fontWeight="bold">
                    لقد وفرت {totalSavings.toFixed(2)} جنيه مصري !
                  </Typography>
                  <Typography variant="body2" color="success.dark">
                    نسبة خصم {((totalSavings / originalTotal) * 100).toFixed(1)}% من طلبك
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Order Items */}
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingBag /> منتجات الطلب
            </Typography>
            
            <List>
              {order.items.map((item) => {
                const originalPrice = item.original_price || item.price;
                const isDiscounted = originalPrice > item.price;
                const itemSavings = (originalPrice - item.price) * item.quantity;

                return (
                  <ListItem key={item.id} sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                      <img
                        src={item.product?.imageUrl || noPhoto }
                        alt={item.product?.name}
                        style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1 }}
                      />
                      
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {item.product?.name}
                        </Typography>
                        
                        {isDiscounted ? (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="error" fontWeight="bold">
                              E.G.P {item.price} × {item.quantity}
                            </Typography>
                            <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                              E.G.P {originalPrice.toFixed(2)} each
                            </Typography>
                            {item.discount_percent > 0 && (
                              <Chip
                                label={`${item.discount_percent}% off`}
                                size="small"
                                color="success"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            E.G.P {item.price} × {item.quantity}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          E.G.P {(item.price * item.quantity).toFixed(2)}
                        </Typography>
                        {isDiscounted && (
                          <Typography variant="caption" color="success.main">
                            توفير {itemSavings.toFixed(2)} E.G.P
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Order Totals */}
            <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              {totalSavings > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">القيمة الأساسية:</Typography>
                    <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
                      E.G.P {originalTotal.toFixed(2)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="success.main">
                      <Discount sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                      إجمالي التوفير :
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      -E.G.P {totalSavings.toFixed(2)}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">رسوم التوصيل :</Typography>
                  <Typography variant="body2" color="primary.main">
                    E.G.P {order.delivery_fee}
                  </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" fontWeight="bold">الإجمالي:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  E.G.P {order.total_amount}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="bold">إجمالي الطلب:</Typography>
                <Typography 
                  variant="h5" 
                  fontWeight="bold"
                  color={totalSavings > 0 ? 'success.main' : 'primary.main'}
                >
                  E.G.P {order.total_amount}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Order Details Sidebar */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Shipping Information */}
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalShipping /> معلومات التوصيل
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    عنوان التوصيل:
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1, p: 1.5, backgroundColor: 'grey.50', borderRadius: 1 }}>
                    {order.shipping_address}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Payment Information */}
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Payment /> معلومات الدفع
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    طريقة الدفع
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {order.payment_method.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    تاريخ الطلب:
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {new Date(order.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Store /> الإجراءات
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                  <Button
                    variant="contained"
                    endIcon={<Print />}
                    onClick={() => window.print()}
                    fullWidth
                  >
                    طباعة الفاتورة
                  </Button>
                  <Button
                    variant="outlined"
                    endIcon={<ArrowBack />}
                    onClick={() => navigate('/')}
                    fullWidth
                  >
                    إستكمال التسوق
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default OrderConfirmation;